import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import useAuthUser from '@/composables/useAuthUser'

import { Pagination } from '@/interfaces/paginatedResponse'

import { User } from '@/modules/users/interfaces/user'
import type { UserFilters } from '@/modules/users/interfaces/userFilters'

const defaults = {
	filters: {
		search: '',
		company: undefined,
		status: null,
		role: null,
		group: null,
		department: null,
		supervisor: null,
		sortBy: 'name',
		sortDirection: 'asc',
		perPage: 10,
	},
}

export const useUsersCompanyStore = defineStore('users', () => {
	const { user } = useAuthUser()
	const users = ref<User[]>([])
	const filters = ref<UserFilters>({
		...defaults.filters,
		company: user.value?.companyId,
	})
	const pagination = ref<Pagination>()
	const includes = ref<string[]>([])
	const page = ref<string>('1')
	// Objetos User seleccionados en el filtro "Supervisado por" (filters.supervisor
	// guarda solo los ids; esto alimenta combobox y chips). El filtro admite varios:
	// "los que dependen de Ana o de Luis".
	const supervisorUsers = ref<User[]>([])

	/** Compat: el primero seleccionado, para lo que aún espera uno solo. */
	const supervisorUser = computed(() => supervisorUsers.value[0])

	return {
		users,
		filters,
		pagination,
		includes,
		page,
		supervisorUsers,
		supervisorUser,

		setSupervisorUser(newSupervisor: User | undefined) {
			supervisorUsers.value = newSupervisor ? [newSupervisor] : []
			filters.value.supervisor = newSupervisor?.id ?? null
		},

		/**
		 * Con la lista vacía se manda `null` y no `[]`: el backend descarta el
		 * arreglo vacío igual, pero `null` es lo que el resto del código ya
		 * interpreta como "sin filtro" al limpiar.
		 */
		setSupervisorUsers(newSupervisors: User[]) {
			supervisorUsers.value = newSupervisors
			filters.value.supervisor =
				newSupervisors.length ? newSupervisors.map((s) => s.id) : null
		},

		toggleSupervisorUser(supervisor: User) {
			const selected = supervisorUsers.value
			const already = selected.some((s) => s.id === supervisor.id)

			this.setSupervisorUsers(
				already ?
					selected.filter((s) => s.id !== supervisor.id)
				:	[...selected, supervisor]
			)
		},

		setUsers(newusers: User[]) {
			users.value = []
			users.value = newusers
		},

		setFiltersValue<T extends keyof UserFilters>(
			property: T,
			value: UserFilters[T]
		) {
			filters.value[property] = value
		},

		setPagination(newMeta: Pagination) {
			pagination.value = newMeta
		},

		setIncludes(newIncludes: string[]) {
			includes.value = newIncludes
		},

		setPage(newPage: string) {
			page.value = newPage
		},

		resetFilters() {
			const currentCompanyId = filters.value.company
			Object.assign(filters.value, {
				...defaults.filters,
				company: currentCompanyId,
			})
			supervisorUsers.value = []
		},

		resetState() {
			users.value = []
		},
	}
})
